import React from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import objectSupport from 'dayjs/plugin/objectSupport'
import classNames from 'classnames'
import { useMeasure } from 'react-use'
import { useLocalStorageState } from 'ahooks'
import { useIntl } from 'react-intl'
import {
  Button,
  Flex,
  Radio,
  Table,
  Splitter,
  Form,
  Select,
  DatePicker,
  Card,
  Collapse,
} from 'antd'
import { useRepairs } from '../hooks/Repairs'
import { usePlantTestZones } from '../hooks/TestZones'
import { PlantButton } from './PlantButton'
import { useUserPlant } from '../hooks/User'
import { useUserPlantInfo } from '../hooks/Plants'
import { VinURL } from './Reports'
import ExportButton from './RepairByUserExportButton'
import ShiftSelect, {
  getAllShiftsItem,
  getShiftOptions,
  getShiftDateTime,
} from './ShiftSelect'
import { useShifts } from '../hooks/Shifts'

dayjs.extend(objectSupport)
dayjs.extend(utc)
dayjs.extend(timezone)

const RepairZoneSelect = ({ plantName, ...rest }) => {
  const [userPlant] = useUserPlant()
  const { isSuccess, isLoading, isFetching, testZones } = usePlantTestZones({
    plantName: userPlant,
  })

  return (
    <Select
      style={{ minWidth: 150 }}
      loading={isLoading}
      options={testZones?.map((zone) => ({
        value: zone.specifier,
        label: zone.description,
      }))}
      {...rest}
    />
  )
}

const RepairsByUserForm = ({ onFinish, reportsTimeFilters }) => {
  const intl = useIntl()
  const [form] = Form.useForm()
  const allShiftsItem = getAllShiftsItem()
  const [reportsQuery, setReportsQuery] = useLocalStorageState(
    'reports-query',
    {
      defaultValue: {
        start_at: null,
        end_at: null,
        shift: null,
        period: { start: null, end: null },
      },
      listenStorageChange: true,
    },
  )

  // Update localStorage when form values change
  const handleFormChange = (changedFields, allFields) => {
    const periodField = allFields.find((f) => f.name[0] === 'period')
    const shiftField = allFields.find((f) => f.name[0] === 'shift')

    if (periodField?.value && shiftField?.value) {
      setReportsQuery({
        ...reportsQuery,
        shift: shiftField.value,
        period: {
          start: periodField.value[0],
          end: periodField.value[1],
        },
      })
    }
  }

  // Use Reports time filters if available, otherwise use defaults
  const getInitialPeriod = () => {
    if (reportsQuery?.period?.start && reportsQuery?.period?.end) {
      return [dayjs(reportsQuery.period.start), dayjs(reportsQuery.period.end)]
    }
    return [dayjs().startOf('day'), dayjs().endOf('day')]
  }

  const getInitialShift = () => {
    return reportsQuery?.shift || allShiftsItem.name
  }

  // Automatically sync form values when localStorage changes
  React.useEffect(() => {
    form.setFieldsValue({
      shift: getInitialShift(),
      period: getInitialPeriod(),
    })
  }, [reportsQuery, form])

  return (
    <Form
      name="basic"
      style={{ width: '70%' }}
      onFinish={onFinish}
      autoComplete="off"
      form={form}
      size="middle"
      layout="inline"
      onFieldsChange={handleFormChange}
      initialValues={{
        shift: getInitialShift(),
        period: getInitialPeriod(),
      }}
    >
      <Form.Item
        label="Repair Zone"
        name="repairZone"
        rules={[{ required: true, message: 'Please select Repair Zone!' }]}
      >
        <RepairZoneSelect />
      </Form.Item>
      <Form.Item dependencies={['period']}>
        {({ getFieldValue }) => (
          <Form.Item
            name="shift"
            rules={[
              { required: true, message: 'Please select Shift or All Shifts!' },
            ]}
            label={intl.formatMessage({
              id: 'faultReport.filters.shifts.placeholder',
            })}
          >
            <ShiftSelect
              disabled={
                getFieldValue('period')[0].diff(
                  getFieldValue('period')[1],
                  'days',
                ) !== 0
              }
            />
          </Form.Item>
        )}
      </Form.Item>
      <Form.Item
        label={intl.formatMessage({
          id: 'faultReport.filters.shifts.period',
        })}
        name="period"
        rules={[{ required: true, message: 'Please select period!' }]}
      >
        <DatePicker.RangePicker
          onChange={(period) => {
            if (!period[0].isSame(period[1])) {
              form.setFieldValue('shift', allShiftsItem.name)
            }
          }}
        />
      </Form.Item>

      <Form.Item label={null}>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  )
}

const RepairsByUserTable = ({
  repairs,
  isLoading,
  selection,
  setSelection,
  tableHeight,
  startAt,
  endAt,
}) => {
  const [selectedFilters, setSelectedFilters] = React.useState([])
  const groupedAsArray = React.useMemo(() => {
    return Object.values(repairs).map((userGroup) => ({
      created_by_id: userGroup.created_by_id,
      count: userGroup.count,
      reasons: Object.entries(userGroup.reasons).map(([reason, items]) => ({
        reason,
        items,
        count: items.length,
      })),
    }))
  }, [repairs])

  const reasonFilters = React.useMemo(() => {
    const allReasons = Object.values(repairs).flatMap((userGroup) =>
      Object.keys(userGroup.reasons),
    )
    return Array.from(new Set(allReasons)).map((reason) => ({
      text: reason,
      value: reason,
    }))
  }, [repairs])

  const userFilters = React.useMemo(() => {
    return Array.from(
      new Set(
        Object.values(repairs).map((userGroup) => ({
          text: userGroup.created_by_id,
          value: userGroup.created_by_id,
        })),
      ),
    )
  }, [groupedAsArray])

  const columns = [
    {
      title: 'User',
      dataIndex: 'created_by_id',
      key: 'created_by_id',
      filters: [
        {
          text: 'Reasons',
          value: 'reasons',
          children: reasonFilters,
        },
        {
          text: 'Users',
          value: 'users',
          children: userFilters,
        },
      ],
      filterMode: 'tree',
      filterSearch: true,
      filteredValue: selectedFilters,
      onFilter: (value, record) => {
        return (
          record.created_by_id.indexOf(value) === 0 ||
          record.reasons.some((reason) => reason.reason.indexOf(value) === 0)
        )
      },
      render: (value, record, index) => (
        <>
          <Radio
            checked={selection === record.created_by_id}
            onChange={() => setSelection(record.created_by_id)}
          />
          <Button
            onClick={() => setSelection(record.created_by_id)}
            type="link"
          >
            {record.created_by_id}
          </Button>
          <Flex wrap gap="small">
            {record.reasons.map((reason) => (
              <Card
                style={{ display: 'fit-content' }}
                variant="borderless"
                size="small"
                className={classNames('repairs-by-user-reason-card', {
                  filtered: selectedFilters.includes(reason.reason),
                })}
              >
                {reason.reason} ({reason.items.length})
              </Card>
            ))}
          </Flex>
        </>
      ),
    },
    {
      title: 'VIN Count',
      dataIndex: 'reasons',
      key: 'count',
      render: (value, record, index) => record.count,
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.count - b.count,
      width: 150,
      align: 'right',
    },
  ]
  return (
    <>
      <Table
        size="small"
        title={() => (
          <Flex justify="space-between" align="center">
            Repairs by User{' '}
            <ExportButton
              repairsByUser={groupedAsArray}
              startAt={startAt}
              endAt={endAt}
            />
          </Flex>
        )}
        scroll={tableHeight ? { y: tableHeight - 120 } : { y: 400 }} // height of title + header + form area
        pagination={false}
        onChange={(pagination, filters, sorter, extra) => {
          // `created_by_id` is a column name
          // can have both data from `created_by_id` and `reason` values.
          setSelectedFilters(filters.created_by_id || [])
        }}
        loading={isLoading}
        dataSource={groupedAsArray}
        columns={columns}
      />
    </>
  )
}

const UserRepairs = ({ repairs }) => {
  const columns = [
    {
      title: 'VIN',
      dataIndex: 'vin',
      key: 'vin',
      render: (value, record, index) => (
        <VinURL
          vin={value}
          vehicleTestResultId={record.vehicle_test_result_id}
          componentId={record.component_id}
        />
      ),
    },
    {
      title: 'Timestamp',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value, record, index) => dayjs(value).format('MM/DD/YYYY h:mma'),
    },
    {
      title: 'Component',
      dataIndex: 'component_id',
      key: 'component_id',
    },
    {
      title: 'status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Hardware Location ID',
      dataIndex: 'hardware_location_id',
      key: 'hardware_location_id',
    },
    {
      title: 'Annotation',
      dataIndex: 'annotation',
      key: 'annotation',
    },
  ]
  return Object.entries(repairs.reasons).map(([reason, events]) => {
    return (
      <Collapse
        ghost
        collapsible="header"
        items={[
          {
            key: '1',
            label: `${reason} (${events.length})`,
            children: (
              <Table
                size="small"
                pagination={false}
                key={reason}
                dataSource={events}
                columns={columns}
              />
            ),
          },
        ]}
      />
    )
  })
}

export const RepairsByUserTab = () => {
  const [userPlant] = useUserPlant()
  const [selection, setSelection] = React.useState(null)
  const { isLoading, shifts: plantShifts } = useShifts({
    plant_name: userPlant,
  })
  const [containerRef, { height: containerHeight }] = useMeasure()
  const [filters, setFilters] = React.useState({
    startAt: null,
    endAt: null,
    zones: [],
  })

  // Read shared time filters from Reports page
  const [reportsQuery] = useLocalStorageState('reports-query', {
    defaultValue: {
      start_at: null,
      end_at: null,
      shift: null,
      period: { start: null, end: null },
    },
    listenStorageChange: true,
  })
  const {
    isSuccess,
    isLoading: repairsIsLoading,
    isFetching,
    repairs,
  } = useRepairs({
    startAt: filters.startAt,
    endAt: filters.endAt,
    zones: [filters.repairZone],
    plants: [userPlant],
  })

  const handleFinish = (values) => {
    setSelection(null)
    const shifts = getShiftOptions(plantShifts || [])

    const { period, repairZone, shift } = values

    const { startAt, endAt } = getShiftDateTime({
      period,
      currentShiftName: shift,
      shifts,
    })
    const shiftDateTimeFormat = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]'
    setFilters({
      startAt: startAt.format(shiftDateTimeFormat),
      endAt: endAt.format(shiftDateTimeFormat),
      repairZone,
    })
  }

  const groupedByUser = React.useMemo(() => {
    if (!repairs) return []
    return repairs.reduce((acc, item) => {
      const created_by_id = item.created_by_id
      if (!acc[created_by_id]) {
        acc[created_by_id] = { created_by_id, reasons: {}, count: 0 }
      }

      const reason = item.reason
      if (!acc[created_by_id].reasons[reason]) {
        acc[created_by_id].reasons[reason] = []
      }
      acc[created_by_id].reasons[reason].push(item)
      acc[created_by_id].count += 1
      return acc
    }, {})
  }, [repairs])

  return (
    <div
      id="repairs-by-user"
      style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <Flex vertical style={{ height: '100%', overflow: 'hidden' }}>
        <Flex gap="large" justify="center" style={{ flexShrink: 0 }}>
          <div style={{ paddingTop: '1px' }}>
            <PlantButton />
          </div>
          <RepairsByUserForm
            onFinish={handleFinish}
            reportsTimeFilters={reportsQuery}
          />
        </Flex>

        <Splitter
          layout="vertical"
          className="repairs-by-user-splitter"
          style={{ flex: 1, minHeight: 0 }}
        >
          <Splitter.Panel defaultSize="50%" min="20%">
            <Flex
              vertical
              gap="large"
              ref={containerRef}
              style={{
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <RepairsByUserTable
                isLoading={repairsIsLoading}
                repairs={groupedByUser}
                selection={selection}
                setSelection={setSelection}
                tableHeight={containerHeight}
                startAt={filters.startAt}
                endAt={filters.endAt}
              />
            </Flex>
          </Splitter.Panel>
          <Splitter.Panel min="20%">
            <Flex vertical>
              {selection && (
                <UserRepairs
                  user={selection}
                  repairs={groupedByUser[selection]}
                />
              )}
            </Flex>
          </Splitter.Panel>
        </Splitter>
      </Flex>
    </div>
  )
}
